import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Bot, AlertCircle, CheckCircle2, XCircle, Sparkles, RefreshCcw,
  PlayCircle, Clock, ExternalLink, FileText, ShieldAlert, TrendingUp,
  ScanLine, Activity, Building, DollarSign,
} from "lucide-react";
import { apiGet, apiPost } from "@/lib/api";
import { toast } from "sonner";
import { AiAnalysisWarning } from "@/components/legal/DataNotes";

/**
 * UsDeepAnalysisSection — 6-specialist Deep AI tab for the US Stock
 * Detail page. Mirrors the CSE Deep Analysis pattern:
 *   • Empty state with "Generate" CTA (admins only during beta)
 *   • Polling job status while a run is in progress
 *   • Renders the persisted shared analysis once complete
 */

const POLL_INTERVAL_MS = 5000;

// Verdict pill colours — match the rest of the US screener palette.
const VERDICT_PILL = {
  // Coordinator overall_verdict
  "STRONG BUY":   "bg-[#14532d] text-white border-[#14532d]",
  "BUY":          "bg-[#16a34a] text-white border-[#16a34a]",
  "HOLD":         "bg-[#6b7280] text-white border-[#6b7280]",
  "REDUCE":       "bg-[#ea580c] text-white border-[#ea580c]",
  "AVOID":        "bg-[#dc2626] text-white border-[#dc2626]",
  // Valuation analyst
  "DEEP VALUE":   "bg-[#14532d] text-white border-[#14532d]",
  "UNDERVALUED":  "bg-[#16a34a] text-white border-[#16a34a]",
  "FAIR VALUE":   "bg-[#6b7280] text-white border-[#6b7280]",
  "OVERVALUED":   "bg-[#ea580c] text-white border-[#ea580c]",
  "OVERPRICED":   "bg-[#dc2626] text-white border-[#dc2626]",
  // Quality
  "EXCEPTIONAL":  "bg-[#14532d] text-white border-[#14532d]",
  "HIGH":         "bg-[#16a34a] text-white border-[#16a34a]",
  "GOOD":         "bg-[#16a34a] text-white border-[#16a34a]",
  "AVERAGE":      "bg-[#6b7280] text-white border-[#6b7280]",
  "WEAK":         "bg-[#dc2626] text-white border-[#dc2626]",
  // Health
  "FORTRESS":     "bg-[#14532d] text-white border-[#14532d]",
  "STRONG":       "bg-[#16a34a] text-white border-[#16a34a]",
  "ADEQUATE":     "bg-[#6b7280] text-white border-[#6b7280]",
  "STRETCHED":    "bg-[#ea580c] text-white border-[#ea580c]",
  "DISTRESSED":   "bg-[#dc2626] text-white border-[#dc2626]",
  // Growth
  "ACCELERATING": "bg-[#14532d] text-white border-[#14532d]",
  "STEADY":       "bg-[#6b7280] text-white border-[#6b7280]",
  "SLOWING":      "bg-[#ea580c] text-white border-[#ea580c]",
  "CONTRACTING":  "bg-[#dc2626] text-white border-[#dc2626]",
  // Risk (HIGHER score = LOWER risk)
  "LOW":          "bg-[#14532d] text-white border-[#14532d]",
  "MODERATE":     "bg-[#16a34a] text-white border-[#16a34a]",
  "ELEVATED":     "bg-[#ea580c] text-white border-[#ea580c]",
  "VERY HIGH":    "bg-[#dc2626] text-white border-[#dc2626]",
  // Technical
  "POSITIVE":     "bg-[#16a34a] text-white border-[#16a34a]",
  "NEUTRAL":      "bg-[#6b7280] text-white border-[#6b7280]",
  "NEGATIVE":     "bg-[#ea580c] text-white border-[#ea580c]",
};

const verdictPill = (verdict) => {
  if (!verdict) return "bg-muted text-muted-foreground";
  const k = String(verdict).trim().toUpperCase();
  return VERDICT_PILL[k] || "bg-muted text-muted-foreground border";
};

const SPECIALIST_META = [
  { key: "valuation_analysis", label: "Valuation Analyst",   Icon: DollarSign },
  { key: "business_quality",   label: "Business Quality",    Icon: Building },
  { key: "financial_health",   label: "Financial Health",    Icon: Activity },
  { key: "growth_prospects",   label: "Growth Prospects",    Icon: TrendingUp },
  { key: "risk_assessment",    label: "Risk Assessment",     Icon: ShieldAlert },
  { key: "technical_momentum", label: "Technical & Momentum", Icon: ScanLine },
];

const fmtDate = (v) => {
  if (!v) return "—";
  try { return new Date(v).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }); }
  catch { return v; }
};

const SpecialistCard = ({ keyName, label, Icon, payload }) => {
  if (!payload) return null;
  const verdict = payload.verdict || payload.overall_verdict;
  const score = payload.score;
  return (
    <Card data-testid={`us-deep-specialist-${keyName}`}>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between gap-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Icon className="h-4 w-4 text-muted-foreground" />
            {label}
          </CardTitle>
          {typeof score === "number" && (
            <span className="text-xs tabular-nums text-muted-foreground">
              {score}/100
            </span>
          )}
        </div>
        {verdict && (
          <div>
            <Badge variant="outline" className={`mt-1 ${verdictPill(verdict)} font-semibold`}>
              {verdict}
            </Badge>
          </div>
        )}
      </CardHeader>
      <CardContent className="text-sm space-y-3 pt-0">
        {payload.summary && (
          <p className="text-foreground/90 leading-relaxed">{payload.summary}</p>
        )}
        {Array.isArray(payload.key_points) && payload.key_points.length > 0 && (
          <ul className="space-y-1.5 text-sm">
            {payload.key_points.map((p, i) => (
              <li key={i} className="flex gap-2">
                <span className="text-muted-foreground mt-0.5">•</span>
                <span className="text-foreground/85">{p}</span>
              </li>
            ))}
          </ul>
        )}
        {Array.isArray(payload.key_risks) && payload.key_risks.length > 0 && (
          <div>
            <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1.5">Key Risks</div>
            <ul className="space-y-1.5 text-sm">
              {payload.key_risks.map((r, i) => (
                <li key={i} className="flex gap-2">
                  <AlertCircle className="h-3.5 w-3.5 text-orange-600 mt-0.5 shrink-0" />
                  <span className="text-foreground/85">{r}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
        {/* Specialist-specific sub-fields */}
        {Object.entries(payload)
          .filter(([k]) => !["verdict","summary","key_points","score","key_risks"].includes(k))
          .map(([k, v]) => (typeof v === "string" && v.length > 0 ? (
            <div key={k} className="text-xs">
              <span className="font-medium text-muted-foreground uppercase tracking-wide">
                {k.replace(/_/g, " ")}:&nbsp;
              </span>
              <span className="text-foreground/85">{v}</span>
            </div>
          ) : null))}
      </CardContent>
    </Card>
  );
};

const ProgressPanel = ({ status }) => {
  const p = status?.progress || {};
  const pct = Math.round(((p.completed_stages || 0) / (p.total_stages || 1)) * 100);
  return (
    <Card data-testid="us-deep-progress-panel">
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <Bot className="h-5 w-5 text-primary animate-pulse" />
          Generating analysis…
        </CardTitle>
        <CardDescription>
          {p.stage_label || "Working through specialist views — usually completes in ~90 seconds."}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <Progress value={pct} className="h-2" />
        <div className="text-xs text-muted-foreground tabular-nums flex items-center justify-between">
          <span>Stage {p.completed_stages || 0} / {p.total_stages || 4}</span>
          <span><Clock className="h-3 w-3 inline mr-1" />{p.elapsed_seconds || 0}s elapsed</span>
        </div>
      </CardContent>
    </Card>
  );
};

const QuotaBadge = ({ quota }) => {
  if (!quota) return null;
  if (quota.is_admin) {
    return <Badge variant="outline" className="bg-violet-500/10 text-violet-700 dark:text-violet-300 border-violet-500/30">Admin · Unlimited</Badge>;
  }
  return (
    <Badge
      variant="outline"
      data-testid="us-deep-quota-badge"
      className="text-xs"
    >
      {quota.quota_used} of {quota.quota_limit} used this month
    </Badge>
  );
};

const UsDeepAnalysisSection = ({ symbol }) => {
  const [status, setStatus] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [quota, setQuota] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);

  const refreshAll = useCallback(async () => {
    if (!symbol) return;
    try {
      const [statusRes, dataRes] = await Promise.all([
        apiGet(`/us-stocks/${encodeURIComponent(symbol)}/deep-analysis/status`),
        apiGet(`/us-stocks/${encodeURIComponent(symbol)}/deep-analysis`),
      ]);
      setStatus(statusRes || null);
      setQuota(statusRes?.user_quota_limit !== undefined ? {
        is_admin: statusRes.is_admin,
        quota_used: statusRes.user_quota_used,
        quota_limit: statusRes.user_quota_limit,
        quota_remaining: statusRes.user_quota_remaining,
      } : (dataRes?.quota || null));
      if (dataRes?.has_analysis) {
        setAnalysis(dataRes.shared);
        setErrorMsg(null);
      } else {
        setAnalysis(null);
      }
    } catch (e) {
      // 403 = not premium — silent. Other errors surface at most once.
      if (e?.status === 403) {
        setErrorMsg("Deep AI is available on Premium plans.");
      } else if (e?.status && e.status !== 404) {
        setErrorMsg(e?.message || "Failed to load Deep AI status");
      }
    } finally {
      setLoading(false);
    }
  }, [symbol]);

  // Initial load
  useEffect(() => {
    setLoading(true);
    refreshAll();
  }, [refreshAll]);

  // Poll while a job is running
  useEffect(() => {
    if (!status?.is_running) return undefined;
    const t = setInterval(() => { refreshAll(); }, POLL_INTERVAL_MS);
    return () => clearInterval(t);
  }, [status?.is_running, refreshAll]);

  const generate = useCallback(async (force = false) => {
    setGenerating(true);
    setErrorMsg(null);
    try {
      const res = await apiPost(
        `/us-stocks/${encodeURIComponent(symbol)}/deep-analysis`,
        { force },
      );
      const kind = res?.status;
      if (kind === "started") {
        toast.success("Analysis started — usually completes in ~90 seconds.");
      } else if (kind === "running") {
        toast.info("Analysis already in progress.");
      } else if (kind === "reused") {
        toast.success("A cached analysis was already available.");
      } else if (kind === "busy") {
        toast.warning(res?.message || "Server is busy with another analysis.");
      }
      await refreshAll();
    } catch (e) {
      const msg = e?.body?.detail || e?.message || "Failed to start analysis";
      setErrorMsg(msg);
      toast.error(msg);
    } finally {
      setGenerating(false);
    }
  }, [symbol, refreshAll]);

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  if (errorMsg && !analysis && !status?.is_running) {
    return (
      <Alert variant="destructive" data-testid="us-deep-error">
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>Deep AI unavailable</AlertTitle>
        <AlertDescription>{errorMsg}</AlertDescription>
      </Alert>
    );
  }

  const isAdmin = !!status?.is_admin;
  const isRunning = !!status?.is_running;
  const lastError = status?.job_status === "failed" ? status?.job_error : null;
  const coord = analysis?.coordinator || {};
  const tenK = analysis?.data_sources || {};

  return (
    <div className="space-y-5" data-testid="us-deep-analysis-section">
      {/* ---- Header ---- */}
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <CardTitle className="flex items-center gap-2 text-xl">
                <Bot className="h-5 w-5 text-primary" />
                Deep AI Analysis
              </CardTitle>
              <CardDescription className="mt-1">
                6-specialist analysis using SEC 10-K filings, fundamentals & analyst consensus.
                Cached for 30 days · powered by Claude Sonnet 4.5.
              </CardDescription>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <QuotaBadge quota={quota} />
              {isAdmin && (analysis || isRunning) && (
                <Button
                  size="sm"
                  variant="outline"
                  disabled={generating || isRunning}
                  onClick={() => generate(true)}
                  data-testid="us-deep-regenerate-btn"
                >
                  <RefreshCcw className="h-3.5 w-3.5 mr-1.5" />
                  Re-run
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* ---- Last failure (if any) ---- */}
      {lastError && (
        <Alert variant="destructive" data-testid="us-deep-last-error">
          <XCircle className="h-4 w-4" />
          <AlertTitle>Previous run failed</AlertTitle>
          <AlertDescription>{lastError}</AlertDescription>
        </Alert>
      )}

      {/* ---- In-progress polling panel ---- */}
      {isRunning && <ProgressPanel status={status} />}

      {/* ---- Empty state ---- */}
      {!analysis && !isRunning && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Sparkles className="h-4 w-4 text-primary" />
              No analysis available yet
            </CardTitle>
            <CardDescription>
              Deep AI analyses this stock using SEC 10-K filings (Risk Factors, MD&A, Business)
              + yfinance financials + sell-side analyst consensus. Cost ~$0.30 per run, valid
              for 30 days, shared across all users.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {isAdmin ? (
              <Button
                onClick={() => generate(false)}
                disabled={generating}
                data-testid="us-deep-generate-btn"
              >
                <PlayCircle className="h-4 w-4 mr-2" />
                {generating ? "Starting…" : "Generate analysis"}
              </Button>
            ) : (
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  Running new Deep AI analyses is restricted to administrators during the beta.
                  You can view shared analyses here once they have been generated.
                </AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>
      )}

      {/* ---- Analysis content ---- */}
      {analysis && (
        <>
          <Card>
            <CardHeader className="pb-3">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <CardTitle className="text-lg">
                    {analysis.company_name || analysis.symbol} — Verdict
                  </CardTitle>
                  <CardDescription className="mt-1">
                    Generated {fmtDate(analysis.generated_at)} ·
                    Valid until {fmtDate(analysis.valid_until)} ·
                    {analysis.view_count ? ` ${analysis.view_count} views` : ""}
                  </CardDescription>
                </div>
                <div className="flex flex-wrap gap-2">
                  {coord.overall_verdict && (
                    <Badge variant="outline" data-testid="us-deep-overall-verdict" className={`${verdictPill(coord.overall_verdict)} text-sm font-bold px-3 py-1`}>
                      {coord.overall_verdict}
                    </Badge>
                  )}
                  {coord.conviction && (
                    <Badge variant="outline" className="text-xs">
                      Conviction: {coord.conviction}
                    </Badge>
                  )}
                  {coord.position_sizing && (
                    <Badge variant="outline" className="text-xs">
                      {coord.position_sizing}
                    </Badge>
                  )}
                  {coord.time_horizon && (
                    <Badge variant="outline" className="text-xs">
                      {coord.time_horizon} term
                    </Badge>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {coord.summary && (
                <p className="text-sm leading-relaxed text-foreground/90 whitespace-pre-line" data-testid="us-deep-coordinator-summary">
                  {coord.summary}
                </p>
              )}

              <div className="grid md:grid-cols-2 gap-4">
                {Array.isArray(coord.bull_case) && coord.bull_case.length > 0 && (
                  <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-4" data-testid="us-deep-bull-case">
                    <div className="text-xs font-bold uppercase tracking-wider text-emerald-700 dark:text-emerald-400 mb-2 flex items-center gap-1.5">
                      <CheckCircle2 className="h-3.5 w-3.5" />
                      Bull Case
                    </div>
                    <ul className="space-y-2 text-sm">
                      {coord.bull_case.map((p, i) => (
                        <li key={i} className="flex gap-2 leading-relaxed">
                          <span className="text-emerald-600 dark:text-emerald-400 font-bold mt-0.5">✓</span>
                          <span className="text-foreground/85">{p}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {Array.isArray(coord.bear_case) && coord.bear_case.length > 0 && (
                  <div className="rounded-lg border border-red-500/30 bg-red-500/5 p-4" data-testid="us-deep-bear-case">
                    <div className="text-xs font-bold uppercase tracking-wider text-red-700 dark:text-red-400 mb-2 flex items-center gap-1.5">
                      <AlertCircle className="h-3.5 w-3.5" />
                      Bear Case
                    </div>
                    <ul className="space-y-2 text-sm">
                      {coord.bear_case.map((p, i) => (
                        <li key={i} className="flex gap-2 leading-relaxed">
                          <span className="text-red-600 dark:text-red-400 font-bold mt-0.5">⚠</span>
                          <span className="text-foreground/85">{p}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              {Array.isArray(coord.catalysts) && coord.catalysts.length > 0 && (
                <div>
                  <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-1.5">
                    Key Catalysts to Watch
                  </div>
                  <ul className="space-y-1 text-sm">
                    {coord.catalysts.map((c, i) => (
                      <li key={i} className="flex gap-2">
                        <span className="text-muted-foreground">→</span>
                        <span className="text-foreground/85">{c}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {Array.isArray(coord.key_metrics_to_watch) && coord.key_metrics_to_watch.length > 0 && (
                <div className="text-xs">
                  <span className="font-medium text-muted-foreground uppercase tracking-wide">Metrics to monitor: </span>
                  <span className="text-foreground/80">{coord.key_metrics_to_watch.join(" · ")}</span>
                </div>
              )}
            </CardContent>
          </Card>

          {/* ---- Data sources banner ---- */}
          <Alert
            variant={tenK.sec_10k_available ? "default" : "destructive"}
            data-testid="us-deep-data-sources"
          >
            <FileText className="h-4 w-4" />
            <AlertTitle>
              {tenK.sec_10k_available
                ? `Source: SEC ${tenK.sec_10k_form || "10-K"} filing (${tenK.sec_10k_filing_year || "—"})`
                : "Source: yfinance fundamentals + analyst consensus only"}
            </AlertTitle>
            <AlertDescription className="text-xs flex items-center justify-between gap-2 flex-wrap">
              <span>
                {tenK.sec_10k_available
                  ? `${tenK.sec_10k_sections_extracted || 0} of 4 sections extracted (Item 1, 1A, 7, 8).`
                  : "10-K filing was not available for this ticker — analysis ran in degraded mode."}
              </span>
              {tenK.sec_10k_url && (
                <a
                  href={tenK.sec_10k_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline inline-flex items-center gap-1"
                  data-testid="us-deep-10k-link"
                >
                  Open filing on SEC.gov
                  <ExternalLink className="h-3 w-3" />
                </a>
              )}
            </AlertDescription>
          </Alert>

          {/* ---- 6 specialists in expandable accordion ---- */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Specialist Analyses</CardTitle>
              <CardDescription>Click any card to expand the specialist's full reasoning.</CardDescription>
            </CardHeader>
            <CardContent>
              <Accordion type="multiple" className="w-full" data-testid="us-deep-specialists-accordion">
                {SPECIALIST_META.map(({ key, label, Icon }) => {
                  const payload = analysis[key];
                  if (!payload) return null;
                  const verdict = payload.verdict;
                  return (
                    <AccordionItem value={key} key={key} data-testid={`us-deep-spec-item-${key}`}>
                      <AccordionTrigger className="hover:no-underline">
                        <div className="flex items-center gap-3 w-full mr-2">
                          <Icon className="h-4 w-4 text-muted-foreground shrink-0" />
                          <span className="font-medium">{label}</span>
                          <span className="flex-1" />
                          {typeof payload.score === "number" && (
                            <span className="text-xs tabular-nums text-muted-foreground">
                              {payload.score}/100
                            </span>
                          )}
                          {verdict && (
                            <Badge variant="outline" className={`${verdictPill(verdict)} text-[10px] font-semibold whitespace-nowrap`}>
                              {verdict}
                            </Badge>
                          )}
                        </div>
                      </AccordionTrigger>
                      <AccordionContent>
                        <SpecialistCard keyName={key} label={label} Icon={Icon} payload={payload} />
                      </AccordionContent>
                    </AccordionItem>
                  );
                })}
              </Accordion>
            </CardContent>
          </Card>
          <AiAnalysisWarning testId="us-deep-analysis-warning" />
        </>
      )}
    </div>
  );
};

export default UsDeepAnalysisSection;
